import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { catalogEntries, catalogTags } from "@/mocks/catalog";

export default function Catalog() {
  const [activeTag, setActiveTag] = useState("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return catalogEntries.filter((entry) => {
      const tagMatch = activeTag === "全部" || entry.tags.includes(activeTag);
      if (!tagMatch) return false;
      if (!keyword) return true;
      const haystack = [entry.title, entry.desc, entry.type, entry.format, ...entry.tags].join(" ").toLowerCase();
      return haystack.includes(keyword);
    });
  }, [activeTag, query]);

  const reset = () => {
    setActiveTag("全部");
    setQuery("");
  };

  return (
    <section id="catalog" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-content px-4 md:px-8">
        <SectionHeading
          eyebrow="作品目录"
          title="一份可在本页直接筛选检索的条目清单"
          desc="清单里的每一条都代表一个内容方向，配有编号、类型与标签。用下面的标签与搜索框可以即时缩小范围，筛选只作用于本页已收录的条目。"
        />

        <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {catalogTags.map((tag) => {
                const active = tag === activeTag;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag(tag)}
                    aria-pressed={active}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:bg-primary-100/70 hover:text-primary-700"
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            <div className="relative w-full lg:w-72">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索条目名称、标签或形式"
                className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
            <span>
              共收录 <strong className="text-foreground-800">{catalogEntries.length}</strong> 条，当前显示{" "}
              <strong className="text-primary-700">{filtered.length}</strong> 条
            </span>
            {(activeTag !== "全部" || query) && (
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-foreground-600 transition-colors hover:bg-background-200/70 hover:text-foreground-900"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            )}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((entry) => (
              <article
                key={entry.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50"
              >
                <div className="relative h-52 w-full overflow-hidden">
                  <img
                    src={entry.image}
                    alt={`${entry.title}示意封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
                    <span className="rounded-md bg-foreground-950/70 px-2 py-0.5 font-heading text-xs font-medium tracking-wide text-background-50 backdrop-blur-sm">
                      {entry.code}
                    </span>
                    <span className="rounded-md bg-accent-500/95 px-2 py-0.5 text-xs font-medium text-foreground-950">
                      {entry.type}
                    </span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{entry.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{entry.desc}</p>
                  <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-background-200/70 pt-3 text-xs">
                    <div>
                      <dt className="text-foreground-400">常见时段</dt>
                      <dd className="mt-0.5 font-medium text-foreground-700">{entry.period}</dd>
                    </div>
                    <div>
                      <dt className="text-foreground-400">篇幅</dt>
                      <dd className="mt-0.5 font-medium text-foreground-700">{entry.duration}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-foreground-400">收录形式</dt>
                      <dd className="mt-0.5 font-medium text-foreground-700">{entry.format}</dd>
                    </div>
                  </dl>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {entry.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-800">没有符合条件的条目</p>
            <p className="mt-1 text-xs text-foreground-500">试着换个标签，或清空搜索关键词再看看。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}